import React, { useState, useEffect } from 'react';
import { X, Link2, Globe, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { AddressUpdateFlow } from './AddressUpdateFlow';
import { useGoogleBusinessSummary } from '../../hooks/useGoogleBusinessSummary';
import { useWebsiteAssessment } from '../../hooks/useWebsiteAssessment';
import { useQueryClient } from '@tanstack/react-query';

interface BusinessSetupModalProps {
  onClose: () => void;
}

export function BusinessSetupModal({ onClose }: BusinessSetupModalProps) {
  const { data: companyId } = useCurrentCompany();
  const { data: businessSummary } = useGoogleBusinessSummary();
  // Assessment data available for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: _assessment } = useWebsiteAssessment();
  const queryClient = useQueryClient();
  const [showAddressFlow, setShowAddressFlow] = useState(false);
  const [showWebsiteInput, setShowWebsiteInput] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasGoogleLinked, setHasGoogleLinked] = useState(false);
  const [hasWebsiteConfigured, setHasWebsiteConfigured] = useState(false);

  // Check setup status
  useEffect(() => {
    const checkSetup = async () => {
      if (!companyId) return;

      // Check Google Business Profile - check both businessSummary and place_id
      // (place_id indicates linking even if summary fetch failed)
      let hasGoogle = !!businessSummary;
      if (!hasGoogle) {
        try {
          const { data: companyData } = await (supabase as any)
            .from('companies')
            .select('place_id')
            .eq('id', companyId)
            .single();
          hasGoogle = !!(companyData?.place_id);
        } catch {
          hasGoogle = false;
        }
      }
      setHasGoogleLinked(hasGoogle);

      // Check Website
      try {
        const { data, error } = await (supabase as any)
          .from('companies')
          .select('website_url')
          .eq('id', companyId)
          .single();

        if (!error && data?.website_url) {
          setHasWebsiteConfigured(true);
        } else {
          setHasWebsiteConfigured(false);
        }
      } catch {
        setHasWebsiteConfigured(false);
      }
    };

    checkSetup();
  }, [companyId, businessSummary]);

  const handleWebsiteSubmit = async () => {
    if (!companyId || !websiteUrl.trim()) return;

    setIsSubmitting(true);
    try {
      // Normalize URL
      let normalizedUrl = websiteUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      const { error } = await (supabase as any)
        .from('companies')
        .update({ website_url: normalizedUrl })
        .eq('id', companyId);

      if (error) throw error;

      // Invalidate website assessment query
      await queryClient.invalidateQueries({ queryKey: ['website-assessment', companyId] });
      
      setHasWebsiteConfigured(true);
      setShowWebsiteInput(false);
    } catch (error) {
      console.error('Error saving website URL:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If both are set up, don't show modal
  if (hasGoogleLinked && hasWebsiteConfigured) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg max-w-2xl w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-text-primary">Complete Your Business Setup</h3>
            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            <p className="text-sm text-text-secondary">
              Set up your Google Business Profile and website to unlock powerful features like review tracking and website assessment.
            </p>

            {/* Google Business Profile Setup */}
            <div className="p-4 bg-[#363232] rounded-lg border border-[#504A4A]">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Link2 className={`w-5 h-5 ${hasGoogleLinked ? 'text-green-400' : 'text-text-tertiary'}`} />
                  <div>
                    <h4 className="text-sm font-semibold text-text-primary">Google Business Profile</h4>
                    <p className="text-xs text-text-secondary">
                      Link your profile to track reviews and ratings automatically
                    </p>
                  </div>
                </div>
                {hasGoogleLinked && (
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                )}
              </div>
              {!hasGoogleLinked && (
                <Button
                  onClick={() => setShowAddressFlow(true)}
                  className="w-full mt-3 bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                  size="sm"
                >
                  Link Google Business Profile
                </Button>
              )}
            </div>

            {/* Website Setup */}
            <div className="p-4 bg-[#363232] rounded-lg border border-[#504A4A]">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Globe className={`w-5 h-5 ${hasWebsiteConfigured ? 'text-green-400' : 'text-text-tertiary'}`} />
                  <div>
                    <h4 className="text-sm font-semibold text-text-primary">Website URL</h4>
                    <p className="text-xs text-text-secondary">
                      Add your website for comprehensive assessment and monitoring
                    </p>
                  </div>
                </div>
                {hasWebsiteConfigured && (
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                )}
              </div>
              {!hasWebsiteConfigured && (
                <div className="mt-3 space-y-3">
                  {showWebsiteInput ? (
                    <>
                      <input
                        type="text"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://example.com"
                        maxLength={2048}
                        className="w-full px-4 py-2 bg-[#221F1F] border border-[#504A4A] rounded-lg text-text-primary placeholder-[#706A6A] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                        disabled={isSubmitting}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setShowWebsiteInput(false)}
                          className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                          size="sm"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleWebsiteSubmit}
                          disabled={isSubmitting || !websiteUrl.trim()}
                          className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                          size="sm"
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button
                      onClick={() => setShowWebsiteInput(true)}
                      className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                      size="sm"
                    >
                      Add Website URL
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Close Button */}
            {(hasGoogleLinked && hasWebsiteConfigured) && (
              <div className="flex justify-end pt-4 border-t border-[#504A4A]">
                <Button
                  onClick={onClose}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Address Update Flow */}
      {showAddressFlow && (
        <AddressUpdateFlow
          onClose={() => {
            setShowAddressFlow(false);
            // Don't reload - let React Query refetch naturally
          }}
          onSuccess={async () => {
            setShowAddressFlow(false);
            
            // Check if place_id was saved (even if summary fetch failed)
            if (companyId) {
              try {
                const { data: companyData } = await (supabase as any)
                  .from('companies')
                  .select('place_id')
                  .eq('id', companyId)
                  .single();
                
                // If place_id exists, mark as linked (even if summary isn't ready yet)
                if (companyData?.place_id) {
                  setHasGoogleLinked(true);
                }
              } catch (error) {
                console.error('Error checking place_id:', error);
              }
              
              // Also try to refetch business summary
              await queryClient.invalidateQueries({ queryKey: ['google-business-summary', companyId] });
              const refreshedSummary = await queryClient.fetchQuery({
                queryKey: ['google-business-summary', companyId],
                queryFn: async () => {
                  const { data, error } = await (supabase as any)
                    .from('google_business_summary')
                    .select('*')
                    .eq('company_id', companyId)
                    .maybeSingle();
                  return error ? null : data;
                },
              });
              // Update based on actual summary data if available
              if (refreshedSummary) {
                setHasGoogleLinked(true);
              }
            }
          }}
        />
      )}
    </>
  );
}
